import { StatusPill, type StatusVariant } from "../internal/status-pill";
import type { ValueTypeEntry } from "../index";
import { EnumEditor, type EnumValue } from "./enum-editor";

export interface EnumOptions {
  /** Available options. Used by the editor; renderer doesn't need this. */
  options?: string[];
  /** Optional per-option variant mapping. Falls back to `neutral`. */
  variantMap?: Record<string, StatusVariant>;
}

/**
 * Built-in `enum` field type. Renders a single `StatusBadge` (never authors
 * a new badge variant — map enum options to existing variants via
 * `variantMap`). Editor shows a focusable button list; click commits,
 * Escape cancels.
 */
export const enumType: ValueTypeEntry<EnumValue, EnumOptions> = {
  name: "enum",
  format(value) {
    return value ?? "";
  },
  render({ value, options }) {
    if (value == null || value === "") return null;
    const variant: StatusVariant = options?.variantMap?.[value] ?? "neutral";
    return <StatusPill variant={variant}>{value}</StatusPill>;
  },
  display({ value, options }) {
    const narrowed = typeof value === "string" ? value : null;
    if (narrowed == null || narrowed === "") return null;
    const variant: StatusVariant = options?.variantMap?.[narrowed] ?? "neutral";
    return <StatusPill variant={variant}>{narrowed}</StatusPill>;
  },
  edit({ value, onCommit, onCancel, options }) {
    return (
      <EnumEditor
        value={value}
        options={options?.options ?? []}
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  },
};
